import moment from 'moment-timezone'

export const createGoalClientId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
    return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`
}

// Subtask tags are comma-separated, lowercase, and whitespace-free.
export const normalizeGoalTags = (tags) => [...new Set(
    (Array.isArray(tags) ? tags : [tags]).flatMap((value) => (
        typeof value === 'string' ? value.split(/,|\n/) : []
    )).map((tag) => tag.replace(/\s/g, '').toLowerCase()).filter(Boolean)
)]

export const matchesGoalTrigger = (goal, session) => {
    if (goal.triggerMode === 'tags') {
        const tags = new Set(normalizeGoalTags(session.tags))
        return normalizeGoalTags(goal.tags).some((tag) => tags.has(tag))
    }
    return (goal.triggers || []).some((trigger) => {
        if (trigger.stampId !== session.stampId) return false
        if (trigger.subtasks === null || trigger.subtasks === undefined) return true
        const subtask = trigger.subtasks.find((candidate) => candidate.name === session.subtaskName)
        if (!subtask) return false
        if (subtask.subsubs === null || subtask.subsubs === undefined) return true
        return Boolean(session.subsubtaskName) && subtask.subsubs.includes(session.subsubtaskName)
    })
}

export const isGoalActiveAt = (goal, committedAt, timezone = moment.tz.guess()) => {
    const campaignStart = moment.tz(`${goal.campaignStartDate} 02:00`, 'YYYY-MM-DD HH:mm', timezone)
    const campaignEnd = moment.tz(`${goal.campaignEndDate} 02:00`, 'YYYY-MM-DD HH:mm', timezone).add(1, 'day')
    const committed = moment(committedAt)
    return !committed.isBefore(campaignStart) && committed.isBefore(campaignEnd)
}

export const createGoalCompletionForEntry = ({
    entry,
    stampId,
    goals,
    timezone = moment.tz.guess(),
    completionId = createGoalClientId(),
}) => {
    const session = {
        completionId,
        clientEntryId: String(entry.id),
        committedAt: moment(entry.done).toISOString(),
        durationMinutes: Math.max(0, Number(entry.duration || 0) / 60000),
        stampId: stampId || '',
        subtaskName: entry.subtaskSname || entry.subsub || '',
        subsubtaskName: entry.subsubName || '',
        tags: normalizeGoalTags(entry.tags),
    }
    const matchingGoals = (goals || []).filter((goal) => (
        isGoalActiveAt(goal, session.committedAt, timezone) && matchesGoalTrigger(goal, session)
    ))

    if (matchingGoals.length === 0) {
        return { entry, pendingCompletion: null, promptCompletion: null }
    }

    const pendingCompletion = {
        ...session,
        goalIds: matchingGoals.map((goal) => String(goal._id)),
        answersByGoal: [],
    }
    const goalsWithQuestions = matchingGoals.filter((goal) => Array.isArray(goal.questions) && goal.questions.length > 0)

    return {
        entry: { ...entry, goalCompletionId: completionId },
        pendingCompletion,
        promptCompletion: goalsWithQuestions.length > 0 ? { ...session, goals: goalsWithQuestions } : null,
    }
}

export const getDefaultGoalAnswers = (goals) => {
    const answers = {}
    for (const goal of goals || []) {
        answers[String(goal._id)] = {}
        for (const question of goal.questions || []) {
            answers[String(goal._id)][question.id] = question.options?.[0]?.id || ''
        }
    }
    return answers
}

export const getGoalTargetLabel = (target, questions = []) => {
    if (target.metric === 'attempts') return 'Attempts'
    if (target.metric === 'durationMinutes') return 'Duration (minutes)'
    const question = questions.find((candidate) => candidate.id === target.questionId)
    if (!question) return target.metric === 'answerCount' ? 'Answer count' : 'Numeric answer sum'
    if (target.metric === 'numericSum') return `${question.label} — sum`
    const option = question.options?.find((candidate) => candidate.id === target.optionId)
    return `${question.label} — ${option?.label || 'answer count'}`
}

export const getDailyTargetGuidance = (target, remainingDays) => {
    const threshold = Number(target?.threshold)
    const actualAtStartOfToday = Number(target?.actualAtStartOfToday)
    const todayActual = Number(target?.todayActual)
    if (![threshold, actualAtStartOfToday, todayActual, remainingDays].every(Number.isFinite) || remainingDays <= 0) {
        return null
    }

    const requiredDailyPace = Math.max(0, threshold - actualAtStartOfToday) / remainingDays
    return {
        remainingToday: Math.max(0, requiredDailyPace - todayActual),
        requiredDailyPace,
        todayActual,
        remainingDays,
    }
}
